export type TopTabKey = 'inspiration' | 'settings' | 'characters' | 'outline' | 'chapters' | 'review' | 'publish';

export interface Chapter {
  id: string;
  backendId?: number;
  index: string;
  title: string;
  progress: number;
  status: 'done' | 'active' | 'todo' | 'stale';
  summary: string;
  words: number;
  targetWords: number;
  content: string;
  score?: number | null;
  review?: Record<string, unknown> | null;
}

export const projectMeta = {
  title: '三点零八分的钟',
  fileName: '三点零八分的钟.lumiu.md',
  platform: '番茄',
  genre: '男频',
};

export const topTabs: Array<{ key: TopTabKey; label: string; count?: string; done?: boolean }> = [
  { key: 'inspiration', label: '灵感', count: '3', done: true },
  { key: 'settings', label: '设定', count: '15/15', done: true },
  { key: 'characters', label: '人物', count: '12', done: true },
  { key: 'outline', label: '大纲', count: '30', done: true },
  { key: 'chapters', label: '章节', count: '5/30' },
  { key: 'review', label: '审稿', count: '82' },
  { key: 'publish', label: '发布', count: '2' },
];

export const chapters: Chapter[] = [
  {
    id: 'ch-01',
    index: '01',
    title: '序 · 钟停了',
    progress: 100,
    status: 'done',
    summary: '主角阿杏发现祖宅老钟停在 3:08，父亲留下第一条禁令。',
    words: 2480,
    targetWords: 1000,
    content: '三点零八分，所有钟声同时停下。阿杏站在祖宅门口，看见父亲的名字被刻在门内侧。',
  },
  {
    id: 'ch-02',
    index: '02',
    title: '阁楼的信封',
    progress: 100,
    status: 'done',
    summary: '阁楼出现封口完好的旧信，寄信时间来自三十年前。',
    words: 2520,
    targetWords: 1000,
    content: '阁楼的木箱里压着一封信。封口完好，邮戳却写着民国三十一年。',
  },
  {
    id: 'ch-03',
    index: '03',
    title: '邮戳是民国',
    progress: 100,
    status: 'done',
    summary: '阿杏追查邮戳来源，第一次听到“钟摆裂纹”的传闻。',
    words: 2360,
    targetWords: 1000,
    content: '邮局旧档案里没有这条寄件记录，只有一张被火燎过的柜台照片。',
  },
  {
    id: 'ch-04',
    index: '04',
    title: '母亲的画像',
    progress: 100,
    status: 'done',
    summary: '画像背后藏着钥匙，母亲的笔迹指向钟楼第十三层。',
    words: 2490,
    targetWords: 1000,
    content: '母亲的画像背面，藏着一枚冷得像雪的铜钥匙。',
  },
  {
    id: 'ch-05',
    index: '05',
    title: '阁楼遗物',
    progress: 68,
    status: 'active',
    summary: '阿杏在阁楼整理祖母遗物。在樟木箱底找到一封三十年前的信，封口完好。同时发现一只青铜怀表，与那座永远停在 3:08 的老钟同款。本章末第一次出现「钟摆裂纹」意象，为第 13 章回收。',
    words: 1142,
    targetWords: 1000,
    content: '那座钟从我祖父的祖父手里就停在三点零八分。家里没人知道为什么 —— 钟身完好，发条上紧后能走两秒，然后又准准地停回去。父亲说这是家族的怪癖，迷信而已。\n\n可那个冬天，我在阁楼整理祖母的遗物时，事情开始有些不对。\n\n樟木箱底压着一封信。信纸已经发黄了，但封口完好，从来没有人拆过。我把它捧到天窗下，借着十二月稀薄的日光看清了邮戳 —— 民国三十一年。收信人那一栏写着',
  },
  {
    id: 'ch-06',
    index: '06',
    title: '第一次回去',
    progress: 0,
    status: 'todo',
    summary: '阿杏第一次触发旧钟回溯，看见祖母年轻时站在阁楼门外。',
    words: 0,
    targetWords: 1000,
    content: '',
  },
];

export const settingsItems = [
  ['平台', '番茄', '强情节、短段落、高爽点密度'],
  ['题材', '都市悬疑', '时间回溯 + 家族旧案'],
  ['主角', '阿杏', '旧钟继承者，第一人称视角'],
  ['目标', '30 章', '每章约 2500 字'],
];

export const questions = [
  '主角最想逃离什么？',
  '旧钟为什么停在 3:08？',
  '父亲为什么隐瞒阁楼？',
  '祖母遗物里谁留下了信？',
  '青铜怀表和老钟是什么关系？',
  '第一次回溯要付出什么代价？',
];

export const outline = [
  ['01-05', '旧钟、阁楼、邮戳、画像、遗物，建立悬疑核心。'],
  ['06-10', '第一次回溯，发现父亲并非隐瞒者，而是守门人。'],
  ['11-15', '钟摆裂纹回收，祖母信件指向第十三章核心反转。'],
  ['16-30', '时间债务扩大，阿杏必须在亲情和真相之间做选择。'],
];

export const foreshadows = [
  ['已埋', '钟摆裂纹', '第 05 章出现，第 13 章回收'],
  ['已埋', '青铜怀表', '第 05 章出现，第 09 章解释规则'],
  ['待收', '父亲禁令', '第 01 章出现，第 18 章反转'],
  ['待收', '民国邮戳', '第 03 章出现，第 12 章推进'],
];

export const characters = [
  ['阿杏', '主角', '旧钟继承者，抗拒家族秘密'],
  ['父亲', '守门人', '把回溯代价伪装成迷信'],
  ['祖母', '钥匙人物', '留下阁楼信件和怀表'],
  ['钟楼老人', '敌友未明', '掌握 3:08 的真实规则'],
];

export const scoreItems = [
  ['节奏', 9, '#19b890'],
  ['爽点', 7, '#bf5a08'],
  ['金手指', 9, '#19b890'],
  ['伏笔', 9, '#19b890'],
  ['人设', 8, '#7c3cff'],
];
